import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, mediaUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Ship, Plus, Anchor, Truck, Pencil, ImagePlus, Camera, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const TYPE_ICONS = { boat: Ship, jetski: Anchor, trailer: Truck };
const STATUS_BADGE_MAP = {
  available: "badge-approved",
  reserved: "badge-protected",
  maintenance: "badge-pending",
  out_of_service: "badge-rejected",
};

export default function Assets() {
  const { user } = useAuth();
  const t = useT();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...asset} = edit
  const canManage = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);

  const reload = async () => {
    const { data } = await api.get("/assets");
    setAssets(data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("assets.subtitle")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("assets.title")}</h2>
        </div>
        {canManage && (
          <Button onClick={() => setEditing({})} data-testid="add-asset-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            <Plus className="w-4 h-4 mr-1" /> {t("assets.add")}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({length: 3}).map((_, i) => <div key={i} className="h-64 ledger-card animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map(a => {
            const Icon = TYPE_ICONS[a.asset_type] || Ship;
            return (
              <div key={a.id} className="ledger-card overflow-hidden hover:-translate-y-0.5 transition-transform relative group" data-testid={`asset-card-${a.id}`}>
                {canManage && (
                  <button
                    onClick={() => setEditing(a)}
                    data-testid={`edit-asset-${a.id}`}
                    className="absolute top-3 right-3 z-10 p-1.5 rounded-md bg-[#050A14]/80 border border-[#1E293B] text-[#94A3B8] hover:text-[#F8FAFC] hover:border-[#4DA6FF] opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit asset"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {a.image_url ? (
                  <div className="relative h-40 overflow-hidden bg-[#050A14]">
                    <img src={a.image_url} alt={a.name} className="w-full h-full object-cover opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1221] via-transparent to-transparent" />
                  </div>
                ) : (
                  <div className="h-40 bg-gradient-to-br from-[#0B1221] to-[#131C31] flex items-center justify-center">
                    <Icon className="w-14 h-14 text-[#4DA6FF]/40" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-display text-lg font-bold tracking-tight">{a.name}</div>
                    <span className={`badge-pill ${STATUS_BADGE_MAP[a.status] || "badge-neutral"}`}>{t(`status.${a.status}`)}</span>
                  </div>
                  <div className="font-mono text-[11px] tracking-widest text-[#94A3B8] uppercase mb-3">{t(`assets.types.${a.asset_type}`)}</div>
                  <div className="space-y-1 text-xs">
                    {a.registration_number && <Row label="REG" value={a.registration_number} />}
                    {a.serial_number && <Row label="SN" value={a.serial_number} />}
                    {a.notes && <div className="text-[#94A3B8] pt-2 border-t border-[#1E293B] mt-2">{a.notes}</div>}
                  </div>
                </div>
              </div>
            );
          })}
          {assets.length === 0 && (
            <div className="col-span-full text-center py-16 text-[#94A3B8]">
              <Ship className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <div className="text-sm">{t("assets.none")}</div>
            </div>
          )}
        </div>
      )}

      {editing !== null && <AssetDialog editing={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="font-mono text-[#94A3B8] w-10">{label}</span>
      <span className="font-mono text-[#F8FAFC]">{value}</span>
    </div>
  );
}

function AssetDialog({ editing, onClose, onSaved }) {
  const t = useT();
  const [form, setForm] = useState(editing ? {
    name: editing.name || "",
    asset_type: editing.asset_type || "boat",
    registration_number: editing.registration_number || "",
    serial_number: editing.serial_number || "",
    status: editing.status || "available",
    notes: editing.notes || "",
    image_url: editing.image_url || "",
  } : { name: "", asset_type: "boat", registration_number: "", serial_number: "", status: "available", notes: "", image_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const isEdit = !!editing;

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("linked_type", "asset");
      if (editing?.id) fd.append("linked_id", editing.id);
      const { data } = await api.post("/media/upload", fd);
      const url = mediaUrl(data.id);
      setForm(f => ({ ...f, image_url: url }));
      toast.success(t("gallery.uploaded", { count: 1 }));
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/assets/${editing.id}`, form);
        toast.success("Asset updated");
      } else {
        await api.post("/assets", form);
        toast.success("Asset added");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-asset-dialog" : "new-asset-dialog"}>
        <DialogHeader><DialogTitle className="font-display tracking-tight">{isEdit ? t("assets.edit") : t("assets.new")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("common.name").toUpperCase()}</Label>
            <Input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} data-testid="asset-name-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("assets.fields.type")}</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {["boat", "jetski", "trailer"].map(ty => (
                <button key={ty} type="button" onClick={() => setForm(f => ({...f, asset_type: ty}))}
                  className={`px-3 py-2 rounded-md border text-xs capitalize ${form.asset_type === ty ? "bg-[#007AFF]/15 border-[#007AFF]/50" : "bg-[#050A14] border-[#1E293B]"}`}
                  data-testid={`asset-type-${ty}`}>{t(`assets.types.${ty}`)}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("assets.fields.status")}</Label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {["available", "reserved", "maintenance", "out_of_service"].map(s => (
                <button key={s} type="button" onClick={() => setForm(f => ({...f, status: s}))}
                  className={`px-2 py-2 rounded-md border text-[10px] font-mono ${form.status === s ? "bg-[#007AFF]/15 border-[#007AFF]/50" : "bg-[#050A14] border-[#1E293B]"}`}
                  data-testid={`asset-status-${s}`}>{t(`status.${s}`)}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("assets.fields.reg")}</Label>
              <Input value={form.registration_number} onChange={(e) => setForm(f => ({...f, registration_number: e.target.value}))} data-testid="asset-reg-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("assets.fields.serial")}</Label>
              <Input value={form.serial_number} onChange={(e) => setForm(f => ({...f, serial_number: e.target.value}))} data-testid="asset-serial-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("assets.fields.image")}</Label>
            <Input value={form.image_url} onChange={(e) => setForm(f => ({...f, image_url: e.target.value}))} placeholder="https://…" data-testid="asset-image-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div className="text-[10px] font-mono tracking-widest text-[#64748B]">{t("assets.fields.imageOr")}</div>
              <input
                ref={fileRef} type="file" accept="image/*,.heic,.heif"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={onPickPhoto} data-testid="asset-image-upload-input"
              />
              <input
                ref={cameraRef} type="file" accept="image/*" capture="environment"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={onPickPhoto} data-testid="asset-image-camera-input"
              />
              <button
                type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="asset-image-upload-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC] disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                {uploading ? t("assets.fields.uploading") : t("assets.fields.uploadPhoto")}
              </button>
              <button
                type="button" onClick={() => cameraRef.current?.click()} disabled={uploading}
                data-testid="asset-image-camera-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC] disabled:opacity-50"
              >
                <Camera className="w-3.5 h-3.5" />
                {t("assets.fields.takePhoto")}
              </button>
            </div>
            {form.image_url && (
              <div className="mt-2 rounded-md overflow-hidden border border-[#1E293B] h-32">
                <img src={form.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("assets.fields.notes")}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} data-testid="asset-notes-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="asset-save-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">{saving ? t("common.saving") : (isEdit ? t("common.saveChanges") : t("common.save"))}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
